/**
 * WHERE AN EVENT IS IN ITS LIFE, from dates it already has.
 *
 * Every event and every collection gets this for free: nothing new is stored.
 * An experience (src/experiences) decides what each stage looks like; this
 * only answers which stage it is, and whether choices can still change.
 *
 *   announce      before registration opens
 *   registration  registration open
 *   before        registration closed, event not started
 *   live          from midnight of the first day until it ends
 *   recap         it has ended
 *   teaser        the experience has announced its next edition (teaserFrom)
 *
 * Choices (breakouts, lunch) have their own clock: Choices_Close_Date, else
 * the start. All comparisons are MP wall clock strings (yyyy-mm-ddTHH:mm:ss).
 */

export type Stage = 'announce' | 'registration' | 'before' | 'live' | 'recap' | 'teaser';
export const STAGES: Stage[] = ['announce', 'registration', 'before', 'live', 'recap', 'teaser'];

export interface StageInput {
  /** Wall clock now; the time machine passes a pretend one. */
  now: string;
  start: string;
  end: string | null;
  registrationOpen: boolean;
  /** Registration has not opened yet. */
  registrationNotYet: boolean;
  choicesCloseAt: string | null;
  /** The experience's next-edition announcement, if it has one. */
  teaserFrom?: string | null;
}

export interface StageInfo {
  stage: Stage;
  choicesLocked: boolean;
  /** What the countdown counts to, when there is one. */
  countdownTo: { at: string; label: string } | null;
}

const day = (wall: string) => wall.slice(0, 10);

export function stageOf(i: StageInput): StageInfo {
  const end = i.end ?? `${day(i.start)}T23:59:59`;
  const stage: Stage =
    i.teaserFrom && i.now >= i.teaserFrom
      ? 'teaser'
      : i.now >= end
        ? 'recap'
        : i.now >= `${day(i.start)}T00:00:00`
          ? 'live'
          : i.registrationOpen
            ? 'registration'
            : i.registrationNotYet
              ? 'announce'
              : 'before';
  const choicesLocked = i.now >= (i.choicesCloseAt ?? i.start);
  const countdownTo =
    stage === 'before' || stage === 'registration' || stage === 'announce'
      ? { at: i.start, label: 'Doors open in' }
      : null;
  return { stage, choicesLocked, countdownTo };
}

/** The church's wall clock now, comparable to MP's values. */
export function nowWall(): string {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Detroit',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? '00';
  const h = g('hour') === '24' ? '00' : g('hour');
  return `${g('year')}-${g('month')}-${g('day')}T${h}:${g('minute')}:${g('second')}`;
}

/** Milliseconds between two wall clock strings (same zone, so plain subtraction). */
export function wallDiff(a: string, b: string): number {
  return Date.parse(`${a}Z`) - Date.parse(`${b}Z`);
}

/** A wall clock string moved by some milliseconds. */
export function wallAdd(wall: string, ms: number): string {
  return new Date(Date.parse(`${wall}Z`) + ms).toISOString().slice(0, 19);
}
